'use client';

import { useEffect, useState } from 'react';
import {
  Pin,
  PinOff,
  Tag,
  ClipboardList,
  Copy,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
} from 'lucide-react';
import { clsx } from 'clsx';
import type {
  FavoriteItem,
  FavoriteLabel,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { BottomSheet, type TagColorId } from '@paeffgen/react/crm-documents/ui';
import {
  formatCurrency,
  TYPE_BADGES,
  TYPE_SHORT,
  useOpenArticleInShop,
} from '@paeffgen/react/crm-documents/util';
import { TagAssignContent } from './tag-assign-content';

export interface FavoriteActionHandlers {
  onTogglePin: () => void;
  onAddToCart: () => void;
  onCopyArticleNumber: () => void;
  onDelete: () => void;
  /** Öffnet die letzte Rechnung (VRG) des Artikels — analog zur Artikelsuche. */
  onOpenLastBeleg: () => void;
  labels: FavoriteLabel[];
  activeLabels: string[];
  onToggleLabel: (label: string) => void;
  onCreateLabel: (name: string, color: TagColorId) => void;
}

interface ContentProps extends FavoriteActionHandlers {
  favorite: FavoriteItem;
  onClose: () => void;
}

// ── Gemeinsamer Inhalt (Menü ⇄ Labels) ───────────────────────────────────────

function FavoriteActionsContent({
  favorite,
  onClose,
  onTogglePin,
  onAddToCart,
  onCopyArticleNumber,
  onDelete,
  onOpenLastBeleg,
  labels,
  activeLabels,
  onToggleLabel,
  onCreateLabel,
}: ContentProps) {
  const [view, setView] = useState<'menu' | 'tags'>('menu');
  const { open: openInShop, isLoading: shopLoading } = useOpenArticleInShop();

  const run = (handler: () => void) => () => {
    onClose();
    handler();
  };

  // Bewusst nicht über run(): der Busy-State soll während der Shop-Query sichtbar
  // bleiben, erst danach schließen — analog zur Artikelsuche.
  const handleOpenShop = async () => {
    await openInShop(favorite.articleNumber);
    onClose();
  };

  // Preisinformation: nur wenn der Artikel jemals berechnet wurde (letzte Rechnung).
  const hasInvoiceData =
    favorite.lastDocumentType != null && favorite.lastDocumentNumber != null;
  const formattedDate = favorite.lastPurchaseDate
    ? new Date(favorite.lastPurchaseDate).toLocaleDateString('de-DE')
    : null;

  if (view === 'tags') {
    return (
      <div className="pb-1">
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100">
          <button
            type="button"
            onClick={() => setView('menu')}
            aria-label="Zurück"
            className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-secondary hover:bg-gray-100"
          >
            <ChevronLeft size={20} />
          </button>
          <span className="text-sm font-bold text-secondary">
            Labels bearbeiten
          </span>
        </div>

        <TagAssignContent
          labels={labels}
          activeLabels={activeLabels}
          onToggle={onToggleLabel}
          onCreate={onCreateLabel}
        />
      </div>
    );
  }

  return (
    <div className="pb-1">
      <button
        type="button"
        onClick={handleOpenShop}
        disabled={shopLoading}
        aria-busy={shopLoading || undefined}
        className="w-full flex items-center gap-3.5 px-4 py-3 text-left hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span
          className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md bg-gray-50 text-gray-700"
          aria-hidden="true"
        >
          <ExternalLink size={18} />
        </span>
        <span className="flex-1 min-w-0 flex flex-col gap-0.5">
          <span className="text-[15px] font-semibold text-secondary leading-tight">
            Im Shop öffnen
          </span>
          <span className="text-xs text-text-muted leading-snug">
            Artikeldetails
          </span>
        </span>
        <ChevronRight
          size={16}
          className="text-gray-300 shrink-0"
          aria-hidden="true"
        />
      </button>
      <div className="border-t border-gray-100 my-1" />
      {hasInvoiceData && (
        <>
          <button
            type="button"
            onClick={run(onOpenLastBeleg)}
            className="w-full flex items-center gap-3.5 px-4 py-3 text-left hover:bg-gray-50"
          >
            <span
              className={clsx(
                'shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md border text-xs font-bold uppercase',
                TYPE_BADGES[favorite.lastDocumentType!],
              )}
              aria-hidden="true"
            >
              {TYPE_SHORT[favorite.lastDocumentType!]}
            </span>
            <span className="flex-1 min-w-0 flex flex-col gap-0.5">
              <span className="text-[15px] font-semibold text-secondary leading-tight">
                Preisinformation
              </span>
              <span className="text-xs text-text-muted leading-snug truncate">
                {favorite.lastNetUnitPrice != null && (
                  <>
                    <span className="font-medium text-gray-700">
                      {formatCurrency(favorite.lastNetUnitPrice)}
                    </span>
                    {' / '}
                    {favorite.unit || 'Stk'}
                    {' · '}
                  </>
                )}
                aus letzter Rechnung{' '}
                <span className="font-mono text-gray-700">
                  {favorite.lastDocumentNumber}
                </span>
                {formattedDate ? <> · {formattedDate}</> : null}
              </span>
            </span>
            <ChevronRight
              size={16}
              className="text-gray-300 shrink-0"
              aria-hidden="true"
            />
          </button>
          <div className="border-t border-gray-100 my-1" />
        </>
      )}
      <button
        type="button"
        onClick={run(onTogglePin)}
        className="w-full flex items-center gap-3.5 px-4 py-3 text-left text-[15px] text-secondary hover:bg-gray-50"
      >
        {favorite.pinned ? (
          <PinOff size={20} className="text-text-muted shrink-0" />
        ) : (
          <Pin size={20} className="text-text-muted shrink-0" />
        )}
        {favorite.pinned ? 'Anpinnen aufheben' : 'Anpinnen'}
      </button>
      <button
        type="button"
        onClick={() => setView('tags')}
        className="w-full flex items-center gap-3.5 px-4 py-3 text-left text-[15px] text-secondary hover:bg-gray-50"
      >
        <Tag size={20} className="text-text-muted shrink-0" />
        Labels bearbeiten
      </button>
      <button
        type="button"
        onClick={run(onAddToCart)}
        className="w-full flex items-center gap-3.5 px-4 py-3 text-left text-[15px] text-secondary hover:bg-gray-50"
      >
        <ClipboardList size={20} className="text-text-muted shrink-0" />
        Zur Anfrage hinzufügen
      </button>
      <button
        type="button"
        onClick={run(onCopyArticleNumber)}
        className="w-full flex items-center gap-3.5 px-4 py-3 text-left text-[15px] text-secondary hover:bg-gray-50"
      >
        <Copy size={20} className="text-text-muted shrink-0" />
        Artikelnummer kopieren
      </button>
      <button
        type="button"
        onClick={run(onDelete)}
        className="w-full flex items-center gap-3.5 px-4 py-3 text-left text-[15px] text-red-600 hover:bg-red-50"
      >
        <Trash2 size={20} className="shrink-0" />
        Aus Favoriten entfernen
      </button>
    </div>
  );
}

// ── Sheet-/Menü-Kopf (Name + Artikelnummer · Preis) ───────────────────────────

function FavoriteHeader({ favorite }: { favorite: FavoriteItem }) {
  return (
    <div className="px-4 py-3 pr-12">
      <div className="text-sm font-bold text-secondary truncate">
        {favorite.description}
      </div>
      <div className="text-xs text-text-muted truncate">
        {favorite.articleNumber}
        {favorite.lastNetUnitPrice != null
          ? ` · ${formatCurrency(favorite.lastNetUnitPrice)}`
          : ''}
      </div>
    </div>
  );
}

// ── Mobile: BottomSheet ───────────────────────────────────────────────────────

interface SheetProps extends FavoriteActionHandlers {
  favorite: FavoriteItem | null;
  open: boolean;
  onClose: () => void;
}

export function FavoriteActionSheet({
  favorite,
  open,
  onClose,
  ...handlers
}: SheetProps): React.JSX.Element {
  return (
    <BottomSheet
      open={open && !!favorite}
      onClose={onClose}
      ariaLabel={favorite ? favorite.description : 'Favoriten-Aktionen'}
      contentKey={favorite?.articleNumber}
      header={favorite ? <FavoriteHeader favorite={favorite} /> : undefined}
    >
      {favorite && (
        <FavoriteActionsContent
          key={favorite.articleNumber}
          favorite={favorite}
          onClose={onClose}
          {...handlers}
        />
      )}
    </BottomSheet>
  );
}

// ── Desktop: Popover am Kebab ─────────────────────────────────────────────────

interface MenuProps extends FavoriteActionHandlers {
  favorite: FavoriteItem | null;
  open: boolean;
  anchor: DOMRect | null;
  onClose: () => void;
}

export function FavoriteActionMenu({
  favorite,
  open,
  anchor,
  onClose,
  ...handlers
}: MenuProps): React.JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !favorite || !anchor) return null;

  // Höhe an den real verfügbaren Platz koppeln statt hart auf 360px begrenzen:
  // nach unten öffnen – bei mehr Platz oberhalb des Kebabs nach oben kippen. So
  // wird nur noch gescrollt, wenn der Inhalt die gewählte Seite wirklich sprengt.
  const MARGIN = 16;
  const GAP = 4;
  const spaceBelow = window.innerHeight - anchor.bottom - MARGIN;
  const spaceAbove = anchor.top - MARGIN;
  const openUp = spaceBelow < spaceAbove;
  const placement = openUp
    ? { bottom: window.innerHeight - anchor.top + GAP, maxHeight: spaceAbove }
    : { top: anchor.bottom + GAP, maxHeight: spaceBelow };

  return (
    <div className="hidden md:block">
      <div
        className="fixed inset-0 z-[90]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="menu"
        aria-label={favorite.description}
        className="fixed z-[100] w-72 overflow-y-auto bg-white rounded-xl shadow-xl border border-gray-200 animate-in fade-in zoom-in-95 duration-150"
        style={{
          ...placement,
          right: Math.max(16, window.innerWidth - anchor.right),
        }}
      >
        <FavoriteHeader favorite={favorite} />
        <FavoriteActionsContent
          key={favorite.articleNumber}
          favorite={favorite}
          onClose={onClose}
          {...handlers}
        />
      </div>
    </div>
  );
}
